import React, { useEffect, useRef, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useShipmentState from '../../utils/useShipmentState';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import ShipmentForm from '../../components/ShipmentForm';
import ShipmentFormProvider from '../../components/ShipmentFormProvider';
import getFullName from '@afosto/utils/getFullName';
import getServicePointAddress from '@afosto/utils-react/shipment/getServicePointAddress';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { methods as methodsApi } from '@afosto/fulfilment/api/Methods';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';

const EditShipment = () => {
  usePrivate();

  const history = useHistory();
  const intl = useIntl();
  const confirm = useConfirm();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:locations:read',
    'rel:contacts:read',
    'rel:contacts:write',
    'lcs:shipments:read',
    'lcs:shipments:write',
  ]);
  const shipmentState = useShipmentState();
  const shipmentStateRef = useRef(shipmentState);
  const { enqueueSnackbar } = useSnackbar();
  const { id: shipmentID } = useParams();
  const [isFetchingShipment, setIsFetchingShipment] = useState(true);
  const { shipment } = shipmentState ?? {};

  useEffect(() => {
    shipmentStateRef.current = shipmentState;
  });

  useEffect(() => {
    let isMounted = true;

    const {
      setContact,
      setFromAddress,
      setFromLocation,
      setMethod,
      setShipment,
      setToAddress,
      setToLocation,
    } = shipmentStateRef?.current ?? {};

    const fetchShipment = async id => {
      try {
        const response = await shipmentsApi.viewShipment(id);
        const { data } = response;

        return data;
      } catch (err) {
        return Promise.reject(err);
      }
    };

    const fetchContact = async id => {
      try {
        const response = await contactsApi.getContact(id);
        const { data } = response;
        const contactData = { ...data, value: data.id, label: getFullName(data) };

        if (isMounted) {
          setContact(contactData);
        }

        return contactData;
      } catch (err) {
        if (isMounted) {
          setContact(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchAddress = async (id, setAddress) => {
      try {
        const response = await addressesApi.viewAddress(id);
        const { data } = response;

        if (isMounted) {
          setAddress(data);
        }

        return data;
      } catch (err) {
        if (isMounted) {
          setAddress(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchLocation = async (id, setLocation) => {
      try {
        const response = await fulfilmentApi.getFulfilmentLocation(id);
        const { data } = response;
        const location = { ...data, value: data.id, label: data.name };

        if (isMounted) {
          setLocation(location);
        }

        return location;
      } catch (err) {
        if (isMounted) {
          setLocation(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchMethod = async (id, carrierSettings) => {
      try {
        const shippingData = {};

        const shippingMethodResponse = await methodsApi.getShippingMethod(id);
        const { data: shippingMethodData = null } = shippingMethodResponse;
        shippingData.method = shippingMethodData
          ? {
              ...shippingMethodData,
              label: shippingMethodData?.name,
              value: shippingMethodData?.id,
            }
          : null;

        if (carrierSettings?.is_carrier_location && shippingMethodData?.id) {
          const servicePointResponse = await fulfilmentApi.getServicePoint(
            carrierSettings?.carrier_location_reference,
            shippingMethodData?.carrier_code,
          );
          const { data: servicePointData = null } = servicePointResponse;
          shippingData.servicePoint = servicePointData
            ? {
                ...servicePointData,
                label: servicePointData?.name,
                value: servicePointData?.id,
              }
            : null;
        }

        if (isMounted && shippingData) {
          setMethod(shippingData);
        }

        return shippingMethodData;
      } catch (err) {
        if (isMounted) {
          setMethod(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchShipmentData = async () => {
      try {
        setIsFetchingShipment(true);

        const currentShipment = await fetchShipment(shipmentID);
        const { addressing, contact_id: contactID, method_id: methodID } = currentShipment;

        const fromId = addressing?.from?.entity_id;
        const fromType = addressing?.from?.entity_type;
        const toId = addressing?.to?.entity_id;
        const toType = addressing?.to?.entity_type;
        const carrierSettings = addressing?.to?.carrier;

        const promises = [
          ...(contactID ? [fetchContact(contactID)] : []),
          ...(fromType === 'address' ? [fetchAddress(fromId, setFromAddress)] : []),
          ...(fromType === 'location' ? [fetchLocation(fromId, setFromLocation)] : []),
          ...(toType === 'address' ? [fetchAddress(toId, setToAddress)] : []),
          ...(toType === 'location' ? [fetchLocation(toId, setToLocation)] : []),
          ...(methodID ? [fetchMethod(methodID, carrierSettings)] : []),
        ];

        await Promise.all(promises);

        if (isMounted) {
          setShipment(currentShipment);
        }
      } catch (err) {
        if (isMounted) {
          setShipment(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingShipment(false);
        }
      }
    };

    if (shipmentID) {
      fetchShipmentData();
    }

    return () => {
      isMounted = false;
    };
  }, [shipmentID]);

  /**
   * Handle cancel of the shipment form.
   */
  const handleCancel = async () => {
    const confirmResponse = await confirm({
      title: intl.formatMessage(translations.cancelDialog.title),
      description: intl.formatMessage(translations.cancelDialog.description),
      catchOnCancel: false,
      buttons: [
        {
          label: intl.formatMessage(translations.cancelDialog.actions.cancel),
          type: 'cancel',
          variant: 'outlined',
          style: {
            marginRight: 'auto',
          },
        },
        {
          label: intl.formatMessage(translations.cancelDialog.actions.ok),
          response: 'ok',
          type: 'submit',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

    if (confirmResponse === 'ok') {
      history.push(`/shipments/${shipment.id}`);
    }
  };

  /**
   * Handle updating the shipment.
   */
  const handleUpdateShipment = async ({
    toType,
    toLocation,
    toAddress,
    fromType,
    fromLocation,
    fromAddress,
    method,
    contact,
    expectedAt,
    shipAt,
  }) => {
    try {
      const carrierLocationAddressID = await getServicePointAddress(method);

      const response = await shipmentsApi.updateShipment(shipment.id, {
        method_id: method?.method?.id ?? null,
        contact_id: contact?.id ?? null,
        addressing: {
          to: {
            entity_type: toType,
            entity_id:
              toType === 'location' ? toLocation?.id : carrierLocationAddressID ?? toAddress?.id,
            ...(method?.method?.is_carrier_location
              ? {
                  carrier: {
                    is_carrier_location: method?.method?.is_carrier_location,
                    carrier_location_reference: method?.servicePoint?.id,
                  },
                }
              : {}),
          },
          from: {
            entity_type: fromType,
            entity_id: fromType === 'location' ? fromLocation?.id : fromAddress?.id,
          },
        },
        expected_at: expectedAt,
        ship_at: shipAt,
      });
      const { data } = response;

      history.push(`/shipments/${data.id}`);
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentNotUpdated), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.editShipment.title)}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (isFetchingShipment) {
    return <SkeletonPage breadcrumbs={3} />;
  }

  if (!shipment) {
    return <h1>No shipment found!</h1>;
  }

  if (!shipment?.is_editable) {
    return <h1>Shipment is not editable!</h1>;
  }

  return (
    <Page
      title={intl.formatMessage(translations.editShipment.title)}
      breadcrumbs={[
        {
          label: intl.formatMessage(translations.breadcrumb.shipments),
          path: '/shipments',
        },
        {
          label: shipment?.number ?? shipment?.id,
          path: `/shipments/${shipmentID}`,
        },
        {
          label: intl.formatMessage(translations.editShipment.title),
          path: `/shipments/edit/${shipmentID}`,
          active: true,
        },
      ]}
      linkComponent={Link}
    >
      <ShipmentFormProvider value={shipmentState}>
        <ShipmentForm onCancel={handleCancel} onSubmit={handleUpdateShipment} />
      </ShipmentFormProvider>
    </Page>
  );
};

export default EditShipment;
